// ============================================================================================
// LES APPLICATIONS DE LA PLATEFORME — une carte par application, qui réunit sa CONNEXION (son enregistrement auprès
// de l'IdP) et sa SURVEILLANCE (la sonde que la Console mesure pour la page Santé).
//
// UTILITÉ : les deux viennent de deux sources sans lien écrit entre elles — les applications de l'IdP sont déclarées
// dans sa configuration, les sondes vivent en base de la Console. Elles se RAPPROCHENT par leur adresse : une sonde
// vise l'URL publique de l'application, et ses adresses de retour sont sur la même origine.
// ⚠️ DÉDUIRE PLUTÔT QUE RELIER À LA MAIN : un lien saisi est un oubli de plus, qui ne se voit qu'à l'usage ; l'origine,
//    elle, est déjà connue des deux côtés (même raisonnement que les retours de déconnexion de l'IdP, `clients.ts`).
// ⚠️ RIEN NE DISPARAÎT : une application de l'IdP qu'aucune sonde ne vise a sa carte, sans surveillance ; une sonde
//    qu'aucune application ne reconnaît a la sienne, sans connexion. Une carte absente se lirait « tout est surveillé ».
// ============================================================================================
import type { RpApp, ServiceHealth } from './api';

export interface PlatformApp {
  /** Clé stable de la carte : la sonde si elle existe, sinon l'application de l'IdP. */
  key: string;
  name: string;
  /** L'origine que l'application sert (« https://bo.orgazone.fr »), ou `null` si l'adresse est illisible. */
  origin: string | null;
  /** L'IdP lui-même : il ne s'enregistre nulle part, il reçoit les enregistrements. */
  provider: boolean;
  service: ServiceHealth | null;
  rp: RpApp | null;
}

/** L'origine d'une adresse, ou `null` quand elle n'en est pas une. */
export function originOf(url: string): string | null {
  try { return new URL(url).origin; } catch { return null; }
}

/**
 * Les cartes de la page Applications.
 * `rps` vaut `null` quand l'IdP n'a pas répondu : les sondes gardent leur carte, leur connexion est alors inconnue.
 * `issuer` est l'origine de l'IdP : c'est elle qui reconnaît la carte du fournisseur de connexion.
 */
export function platformApps(services: readonly ServiceHealth[], rps: readonly RpApp[] | null, issuer: string | null): PlatformApp[] {
  const used = new Set<string>();
  const fromServices = services.map((service): PlatformApp => {
    const origin = originOf(service.url);
    const provider = issuer !== null && origin !== null && origin === issuer;
    const rp = provider || origin === null
      ? null
      : rps?.find((r) => r.redirectUris.some((u) => originOf(u) === origin)) ?? null;
    if (rp) used.add(rp.clientId);
    return { key: `svc:${service.id}`, name: service.name, origin, provider, service, rp };
  });
  const unprobed = (rps ?? []).filter((r) => !used.has(r.clientId)).map((rp): PlatformApp => ({
    key: `rp:${rp.clientId}`,
    name: rp.name,
    origin: rp.redirectUris.map(originOf).find((o): o is string => o !== null) ?? null,
    provider: false,
    service: null,
    rp,
  }));
  // Le fournisseur en tête (les autres s'enregistrent auprès de lui), puis l'ordre alphabétique.
  return [...fromServices, ...unprobed].sort((a, b) => Number(b.provider) - Number(a.provider) || a.name.localeCompare(b.name, 'fr'));
}
